import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bell, Menu, Calendar, Headphones } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import api from '../../lib/api';
import KillSwitchToggle from '../shared/KillSwitchToggle';

const titles = {
  '/dashboard': { title: 'Dashboard', greet: true },
  '/dashboard/products': { title: 'Products' },
  '/dashboard/products/upload': { title: 'Add Products' },
  '/dashboard/categories': { title: 'Categories' },
  '/dashboard/broadcast': { title: 'Catalog Broadcast' },
  '/dashboard/whatsapp': { title: 'WhatsApp Integration' },
  '/dashboard/orders': { title: 'Orders' },
  '/dashboard/payments': { title: 'Payment Verification' },
  '/dashboard/shipping': { title: 'Shipping Management' },
  '/dashboard/inventory': { title: 'Stock & Inventory' },
  '/dashboard/ai': { title: 'AI Assistant' },
  '/dashboard/customers': { title: 'Customers' },
  '/dashboard/support': { title: 'Customer Support' },
  '/dashboard/analytics': { title: 'Reports & Analytics' },
  '/dashboard/notifications': { title: 'Notifications' },
  '/dashboard/settings': { title: 'Settings' },
};

export default function Topbar({ onToggle }) {
  const { seller, refresh } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const meta = titles[pathname] || { title: pathname.split('/').pop()?.replace(/^\w/, (c) => c.toUpperCase()) || 'Dashboard' };
  const [unread, setUnread] = useState(0);
  const [supportOpen, setSupportOpen] = useState(0);
  const [rating, setRating] = useState(null);
  const [profilePhotoOpen, setProfilePhotoOpen] = useState(false);
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoUploading, setProfilePhotoUploading] = useState(false);

  
const [profileAccess, setProfileAccess] = useState(null);

useEffect(() => {
  api.get('/settings/social-access')
    .then(({ data }) => setProfileAccess(data))
    .catch(() => setProfileAccess({ can_edit: false }));
}, []);



const handleProfilePhotoUpload = async (file) => {
  if (!file || !profileAccess?.can_edit) return;

  setProfilePhotoUploading(true);

  try {
    const formData = new FormData();
    formData.append('file', file);

    await api.post('/sellers/profile-photo', formData);
    await refresh();

    setProfilePhotoFile(null);
    setProfilePhotoOpen(false);
  } catch (error) {
    console.error('Profile photo upload failed:', error);
    alert(error?.response?.data?.detail || 'Profile photo upload failed.');
  } finally {
    setProfilePhotoUploading(false);
  }
};


  useEffect(() => {
    const load = () => {
      api.get('/notifications').then(({ data }) => setUnread(data.unread_count || 0)).catch(() => {});
      api.get('/support/count').then(({ data }) => setSupportOpen(data.open || 0)).catch(() => {});
    };
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [pathname]);

  // Rating strip inside the Dashboard header (Feb 2026 approved layout).
  // Fetched once per seller session; endpoint is public and already used
  // on the Dashboard page — reuses that same data source.
  useEffect(() => {
    if (pathname !== '/dashboard' || !seller?.id) return;
    api.get(`/sellers/${seller.id}/rating`).then(({ data }) => setRating(data)).catch(() => {});
  }, [pathname, seller?.id]);

  const initials = (seller?.business_name || seller?.name || 'U').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <header className="h-16 bg-white border-b border-fab-primary-lighter px-3 sm:px-4 lg:px-6 flex items-center justify-between sticky top-0 z-30" data-testid="topbar">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
        <button onClick={onToggle} className="p-2 -ml-1 hover:bg-fab-elevated rounded-lg lg:hidden shrink-0" data-testid="menu-toggle" aria-label="Open menu"><Menu className="w-5 h-5" /></button>
        <div className="min-w-0 flex-1">
          {meta.greet ? (
            <>
              <div className="font-display font-bold text-[15px] sm:text-[18px] text-fab-text truncate">Welcome back, {seller?.business_name || seller?.name} 👋</div>
              {rating && rating.count > 0 ? (
                <div className="flex items-center gap-1.5 text-[11px] sm:text-[12px] truncate" data-testid="topbar-rating">
                  <span className="text-amber-500">★</span>
                  <span className="font-bold text-fab-text">{rating.average} / 5</span>
                  <span className="text-fab-text-muted hidden sm:inline">Based on {rating.count} rating{rating.count === 1 ? '' : 's'}</span>
                </div>
              ) : (
                <div className="text-[11px] sm:text-[12px] text-fab-text-muted truncate hidden sm:block">Here&apos;s what&apos;s happening with your business today.</div>
              )}
            </>
          ) : (
            <div className="font-display font-bold text-[15px] sm:text-[18px] text-fab-text truncate">{meta.title}</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
        <KillSwitchToggle />
        <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-fab-primary-lighter text-[12px] text-fab-text-secondary bg-white">
          <Calendar className="w-4 h-4" /> Last 30 days
        </div>
        <button
          onClick={() => nav('/dashboard/support')}
          className="relative p-2 hover:bg-fab-elevated rounded-lg"
          data-testid="support-bell"
          title="Customer Support Queue"
        >
          <Headphones className={`w-5 h-5 ${supportOpen > 0 ? 'text-red-600' : 'text-fab-text-secondary'}`} />
          {supportOpen > 0 && (
            <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="support-count">{supportOpen}</span>
          )}
        </button>
        <button onClick={() => nav('/dashboard/notifications')} className="relative p-2 hover:bg-fab-elevated rounded-lg" data-testid="notif-bell">
          <Bell className="w-5 h-5 text-fab-text-secondary" />
          {unread > 0 && (
            <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-fab-orange text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-count">{unread}</span>
          )}
        </button>
        <div className="flex items-center gap-2 pl-2 sm:pl-3 sm:border-l sm:border-fab-primary-lighter">
          
<div
  onClick={() => {
  if (profileAccess?.can_edit) {
    setProfilePhotoOpen(true);
  } else {
    nav('/dashboard/settings');
  }
}}
  title="Profile settings"
  className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-fab-primary to-fab-primary-light text-white flex items-center justify-center font-semibold text-[12px] sm:text-[13px] overflow-hidden cursor-pointer"
>
  {profileAccess?.can_edit && seller?.profile_photo_url ? (
    <img
      src={seller.profile_photo_url}
      alt="Seller profile"
      className="w-full h-full object-cover"
    />
  ) : (
    initials
  )}
</div>

          <div className="hidden sm:block">
            <div className="text-[13px] font-semibold text-fab-text leading-tight">{seller?.business_name || seller?.name}</div>
            <div className="text-[10px] text-fab-text-muted uppercase tracking-wider">Seller</div>
          </div>
        </div>
      </div>
    
      {profilePhotoOpen && profileAccess?.can_edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">
              Upload Photo
            </h3>

            <p className="mt-1 text-sm text-gray-600">
              Choose a profile photo for your seller account.
            </p>

            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="mt-4 block w-full text-sm"
              
onChange={async (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  await handleProfilePhotoUpload(file);
  e.target.value = '';
}}

            />

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setProfilePhotoOpen(false);
                  setProfilePhotoFile(null);
                }}
                className="rounded-lg border px-4 py-2 text-sm"
                disabled={profilePhotoUploading}
              >
                Cancel
              </button>

              
            </div>
          </div>
        </div>
      )}
</header>
  );
}
